import * as React from 'react';
import styled, { css, keyframes } from 'styled-components';

import { getSemanticAttributes } from '../../internals/semanticProps';
import { rem } from '../../tokens/spacing';

export interface Step {
  label: string;
  description?: string;
  icon?: string;
}

export type StepsVariant = 'default' | 'compact' | 'pill' | 'dot' | 'numbered';
export type StepsOrientation = 'horizontal' | 'vertical';

export interface StepsProps extends React.HTMLAttributes<HTMLDivElement> {
  steps: Step[];
  currentStep: number;
  variant?: StepsVariant;
  orientation?: StepsOrientation;
  onStepClick?: (index: number) => void;
  semanticId?: string;
  size?: 'sm' | 'md' | 'lg';
  showDescription?: boolean;
  showConnector?: boolean;
}

type StepState = 'completed' | 'active' | 'upcoming';
type StepSize = NonNullable<StepsProps['size']>;

const getStepState = (index: number, currentStep: number): StepState =>
  index < currentStep ? 'completed' : index === currentStep ? 'active' : 'upcoming';

/* ─── Size Maps ─── */

const circleSize: Record<StepSize, number> = { sm: 28, md: 40, lg: 52 };
const circleFont: Record<StepSize, number> = { sm: 11, md: 14, lg: 16 };
const connectorThickness: Record<StepSize, number> = { sm: 2, md: 3, lg: 4 };
const checkSize: Record<StepSize, number> = { sm: 14, md: 16, lg: 20 };
const titleFont: Record<StepSize, number> = { sm: 12, md: 14, lg: 16 };
const descFont: Record<StepSize, number> = { sm: 11, md: 12, lg: 13 };

/* ─── Animations ─── */

const pulseRing = keyframes`
  0% { box-shadow: 0 0 0 0 var(--ui-focus-ring); }
  70% { box-shadow: 0 0 0 6px transparent; }
  100% { box-shadow: 0 0 0 0 transparent; }
`;

const scaleIn = keyframes`
  from { transform: scale(0); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
`;

/* ═══════════════════════════════════════════════
   VARIANT: DEFAULT — Wealthlane wizard stepper
   ═══════════════════════════════════════════════ */

const DefaultRoot = styled.div<{ $orientation: StepsOrientation }>`
  box-sizing: border-box;
  display: flex;
  flex-direction: ${({ $orientation }) => ($orientation === 'vertical' ? 'column' : 'row')};
  flex-wrap: ${({ $orientation }) => ($orientation === 'vertical' ? 'nowrap' : 'wrap')};
  align-items: ${({ $orientation }) => ($orientation === 'vertical' ? 'stretch' : 'flex-start')};
  width: 100%;
  font-family: var(--ui-font-sans);
  overflow-x: auto;
`;

const DefaultSegment = styled.div<{ $orientation: StepsOrientation }>`
  display: flex;
  flex-direction: ${({ $orientation }) => ($orientation === 'vertical' ? 'column' : 'row')};
  align-items: ${({ $orientation }) => ($orientation === 'vertical' ? 'flex-start' : 'center')};
  flex: ${({ $orientation }) => ($orientation === 'vertical' ? '0 0 auto' : '1 1 0')};
  min-width: 0;
`;

const DefaultStepColumn = styled.div<{ $orientation: StepsOrientation }>`
  display: flex;
  flex-direction: ${({ $orientation }) => ($orientation === 'vertical' ? 'row' : 'column')};
  align-items: ${({ $orientation }) => ($orientation === 'vertical' ? 'flex-start' : 'center')};
  flex: 0 0 auto;
  min-width: ${({ $orientation }) => ($orientation === 'vertical' ? 'auto' : rem(80))};
  gap: ${({ $orientation }) => ($orientation === 'vertical' ? rem(12) : '0')};
`;

const DefaultCircle = styled.div.withConfig({
  shouldForwardProp: (p) => !['$state', '$clickable', '$sz'].includes(String(p)),
})<{ $state: StepState; $clickable: boolean; $sz: StepSize }>`
  width: ${({ $sz }) => rem(circleSize[$sz])};
  height: ${({ $sz }) => rem(circleSize[$sz])};
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-weight: 600;
  font-size: ${({ $sz }) => rem(circleFont[$sz])};
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease,
    box-shadow 0.2s ease;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  position: relative;

  ${({ $state }) => {
    switch ($state) {
      case 'completed':
        return css`
          background: var(--ui-interactive);
          color: var(--ui-on-interactive);
          border: 2px solid var(--ui-interactive);
        `;
      case 'active':
        return css`
          background: var(--ui-surface);
          color: var(--ui-interactive);
          border: 2px solid var(--ui-interactive);
          animation: ${pulseRing} 2s ease infinite;
        `;
      case 'upcoming':
      default:
        return css`
          background: var(--ui-surface);
          color: var(--ui-text-subtle);
          border: 2px solid var(--ui-border);
        `;
    }
  }}

  &:hover {
    ${({ $clickable }) =>
      $clickable &&
      css`
        filter: brightness(0.96);
      `}
  }
`;

const DefaultCheckIcon = styled.span<{ $sz: StepSize }>`
  font-size: ${({ $sz }) => `${checkSize[$sz]}px`};
  line-height: 1;
  animation: ${scaleIn} 0.25s ease;
`;

const DefaultConnectorH = styled.div.withConfig({
  shouldForwardProp: (p) => !['$completed', '$sz'].includes(String(p)),
})<{ $completed?: boolean; $sz: StepSize }>`
  flex: 1 1 auto;
  height: ${({ $sz }) => rem(connectorThickness[$sz])};
  margin: 0 ${rem(8)};
  margin-top: ${({ $sz }) => rem(circleSize[$sz] / 2 - connectorThickness[$sz] / 2)};
  border-radius: var(--ui-radius-sm, ${rem(2)});
  background: ${({ $completed }) =>
    $completed ? 'var(--ui-interactive)' : 'var(--ui-border-muted)'};
  transition: background-color 0.3s ease;
`;

const DefaultConnectorV = styled.div.withConfig({
  shouldForwardProp: (p) => !['$completed', '$sz'].includes(String(p)),
})<{ $completed?: boolean; $sz: StepSize }>`
  width: ${({ $sz }) => rem(connectorThickness[$sz])};
  flex: 0 0 auto;
  min-height: ${rem(24)};
  margin: ${rem(4)} 0 ${rem(4)} ${({ $sz }) => rem(circleSize[$sz] / 2 - connectorThickness[$sz] / 2)};
  border-radius: var(--ui-radius-sm, ${rem(2)});
  background: ${({ $completed }) =>
    $completed ? 'var(--ui-interactive)' : 'var(--ui-border-muted)'};
  transition: background-color 0.3s ease;
`;

const DefaultBody = styled.div<{ $orientation: StepsOrientation }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $orientation }) => ($orientation === 'vertical' ? 'flex-start' : 'center')};
  text-align: ${({ $orientation }) => ($orientation === 'vertical' ? 'left' : 'center')};
  gap: ${rem(4)};
  min-width: 0;
  margin-top: ${({ $orientation }) => ($orientation === 'vertical' ? rem(4) : rem(8))};
`;

const DefaultTitle = styled.span<{ $sz: StepSize }>`
  font-size: ${({ $sz }) => rem(titleFont[$sz])};
  font-weight: 600;
  color: var(--ui-text);
`;

const DefaultDesc = styled.span<{ $sz: StepSize }>`
  font-size: ${({ $sz }) => rem(descFont[$sz])};
  color: var(--ui-text-subtle);
  line-height: 1.4;
`;

/* ═══════════════════════════════════════════════
   VARIANT: COMPACT — Breadcrumb-like numbered bar
   Production parity with StepperWrapper
   ═══════════════════════════════════════════════ */

const CompactNav = styled.nav`
  font-family: var(--ui-font-sans);
  background-color: var(--ui-surface);
  border: 1px solid var(--ui-border-muted);
  border-radius: var(--ui-radius-md, ${rem(6)});
  padding: ${rem(27.5)} ${rem(24)};
  display: flex;
  align-items: center;
  gap: ${rem(24)};
`;

const CompactItem = styled.div.withConfig({
  shouldForwardProp: (p) => !['$active', '$clickable'].includes(String(p)),
})<{ $active?: boolean; $clickable?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${rem(6)};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

  &:hover {
    ${({ $clickable, $active }) =>
      $clickable &&
      !$active &&
      css`
        .compact-circle {
          background-color: var(--ui-breadcrumb-active, var(--ui-interactive));
        }
        .compact-label {
          color: var(--ui-text);
        }
      `}
  }
`;

const compactCircleSize: Record<StepSize, number> = { sm: 20, md: 24, lg: 28 };
const compactCircleFont: Record<StepSize, number> = { sm: 10, md: 11, lg: 13 };
const compactLabelFont: Record<StepSize, number> = { sm: 14, md: 15, lg: 16 };
const compactSeparatorFont: Record<StepSize, number> = { sm: 20, md: 22, lg: 24 };

const CompactCircle = styled.span.withConfig({
  shouldForwardProp: (p) => !['$active', '$size'].includes(String(p)),
})<{ $active?: boolean; $size?: StepSize }>`
  width: ${({ $size = 'sm' }) => rem(compactCircleSize[$size])};
  height: ${({ $size = 'sm' }) => rem(compactCircleSize[$size])};
  border-radius: var(--ui-radius-xl, ${rem(20)});
  display: flex;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  transition: background-color 0.2s ease;

  background-color: ${({ $active }) =>
    $active
      ? 'var(--ui-breadcrumb-active, var(--ui-interactive))'
      : 'var(--ui-breadcrumb-inactive, var(--ui-border-strong))'};

  span {
    font-weight: 700;
    font-size: ${({ $size = 'sm' }) => rem(compactCircleFont[$size])};
    line-height: 1;
    color: var(--ui-text-inverse);
  }
`;

const CompactLabel = styled.span.withConfig({
  shouldForwardProp: (p) => !['$active', '$size'].includes(String(p)),
})<{ $active?: boolean; $size?: StepSize }>`
  font-size: ${({ $size = 'sm' }) => rem(compactLabelFont[$size])};
  line-height: 1.3;
  font-weight: 500;
  transition: color 0.2s ease;
  color: ${({ $active }) =>
    $active ? 'var(--ui-text)' : 'var(--ui-breadcrumb-inactive, var(--ui-border-strong))'};
`;

const CompactSeparator = styled.span.withConfig({
  shouldForwardProp: (p) => !['$size'].includes(String(p)),
})<{ $size?: StepSize }>`
  font-size: ${({ $size = 'sm' }) => rem(compactSeparatorFont[$size])};
  color: var(--ui-breadcrumb-separator, var(--ui-text-subtle));
`;

/* ═══════════════════════════════════════════════
   VARIANT: PILL — Modern pill-shaped indicators
   ═══════════════════════════════════════════════ */

const PillRoot = styled.div<{ $orientation: StepsOrientation }>`
  font-family: var(--ui-font-sans);
  display: flex;
  flex-direction: ${({ $orientation }) => ($orientation === 'vertical' ? 'column' : 'row')};
  gap: ${({ $orientation }) => ($orientation === 'vertical' ? rem(4) : rem(6))};
  width: 100%;
`;

const PillSegment = styled.div<{ $orientation: StepsOrientation }>`
  display: flex;
  flex-direction: ${({ $orientation }) => ($orientation === 'vertical' ? 'row' : 'column')};
  align-items: ${({ $orientation }) => ($orientation === 'vertical' ? 'center' : 'stretch')};
  gap: ${({ $orientation }) => ($orientation === 'vertical' ? rem(12) : rem(8))};
  flex: 1 1 0;
  min-width: 0;
`;

const PillBar = styled.div.withConfig({
  shouldForwardProp: (p) => !['$state', '$clickable', '$orientation'].includes(String(p)),
})<{ $state: StepState; $clickable: boolean; $orientation: StepsOrientation }>`
  height: ${({ $orientation }) => ($orientation === 'vertical' ? rem(48) : rem(4))};
  width: ${({ $orientation }) => ($orientation === 'vertical' ? rem(4) : '100%')};
  border-radius: var(--ui-radius-sm, ${rem(4)});
  transition: background-color 0.3s ease, transform 0.15s ease;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

  ${({ $state }) => {
    switch ($state) {
      case 'completed':
        return css`background: var(--ui-interactive);`;
      case 'active':
        return css`background: var(--ui-interactive); opacity: 1;`;
      case 'upcoming':
      default:
        return css`background: var(--ui-border-muted);`;
    }
  }}
`;

const PillMeta = styled.div<{ $orientation: StepsOrientation }>`
  display: flex;
  flex-direction: column;
  gap: ${rem(2)};
  ${({ $orientation }) => $orientation === 'vertical' && css`padding: ${rem(8)} 0;`}
`;

const PillLabel = styled.span.withConfig({
  shouldForwardProp: (p) => !['$state'].includes(String(p)),
})<{ $state: StepState }>`
  font-size: ${rem(12)};
  font-weight: ${({ $state }) => ($state === 'active' ? 600 : 500)};
  color: ${({ $state }) =>
    $state === 'upcoming' ? 'var(--ui-text-subtle)' : 'var(--ui-text)'};
  transition: color 0.2s ease;
  letter-spacing: 0.01em;
`;

const PillDesc = styled.span`
  font-size: ${rem(11)};
  color: var(--ui-text-subtle);
  line-height: 1.35;
`;

const PillNumber = styled.span.withConfig({
  shouldForwardProp: (p) => !['$state'].includes(String(p)),
})<{ $state: StepState }>`
  font-size: ${rem(11)};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ $state }) =>
    $state === 'upcoming' ? 'var(--ui-text-subtle)' : 'var(--ui-interactive)'};
  margin-bottom: ${rem(2)};
`;

/* ═══════════════════════════════════════════════
   VARIANT: DOT — Minimal dot indicator
   ═══════════════════════════════════════════════ */

const DotRoot = styled.div<{ $orientation: StepsOrientation }>`
  font-family: var(--ui-font-sans);
  display: flex;
  flex-direction: ${({ $orientation }) => ($orientation === 'vertical' ? 'column' : 'row')};
  align-items: ${({ $orientation }) => ($orientation === 'vertical' ? 'flex-start' : 'center')};
  gap: ${({ $orientation }) => ($orientation === 'vertical' ? rem(20) : rem(32))};
  width: 100%;
`;

const DotStep = styled.div.withConfig({
  shouldForwardProp: (p) => !['$clickable', '$orientation'].includes(String(p)),
})<{ $clickable: boolean; $orientation: StepsOrientation }>`
  display: flex;
  flex-direction: ${({ $orientation }) => ($orientation === 'vertical' ? 'row' : 'column')};
  align-items: center;
  gap: ${({ $orientation }) => ($orientation === 'vertical' ? rem(12) : rem(8))};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
`;

const DotIndicator = styled.div.withConfig({
  shouldForwardProp: (p) => !['$state'].includes(String(p)),
})<{ $state: StepState }>`
  width: ${rem(10)};
  height: ${rem(10)};
  border-radius: 50%;
  flex-shrink: 0;
  transition: background-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;

  ${({ $state }) => {
    switch ($state) {
      case 'completed':
        return css`
          background: var(--ui-interactive);
          transform: scale(1);
        `;
      case 'active':
        return css`
          background: var(--ui-interactive);
          box-shadow: 0 0 0 4px var(--ui-focus-ring);
          transform: scale(1.2);
        `;
      case 'upcoming':
      default:
        return css`
          background: var(--ui-border-muted);
          transform: scale(1);
        `;
    }
  }}
`;

const DotMeta = styled.div<{ $orientation: StepsOrientation }>`
  display: flex;
  flex-direction: column;
  gap: ${rem(2)};
  text-align: ${({ $orientation }) => ($orientation === 'vertical' ? 'left' : 'center')};
`;

const DotLabel = styled.span.withConfig({
  shouldForwardProp: (p) => !['$state'].includes(String(p)),
})<{ $state: StepState }>`
  font-size: ${rem(13)};
  font-weight: ${({ $state }) => ($state === 'active' ? 600 : 400)};
  color: ${({ $state }) =>
    $state === 'upcoming' ? 'var(--ui-text-subtle)' : 'var(--ui-text)'};
`;

const DotDesc = styled.span`
  font-size: ${rem(11)};
  color: var(--ui-text-subtle);
  line-height: 1.35;
`;

/* ═══════════════════════════════════════════════
   VARIANT: NUMBERED — Bold numbered list
   ═══════════════════════════════════════════════ */

const NumberedRoot = styled.div<{ $orientation: StepsOrientation }>`
  font-family: var(--ui-font-sans);
  display: flex;
  flex-direction: ${({ $orientation }) => ($orientation === 'vertical' ? 'column' : 'row')};
  gap: ${({ $orientation }) => ($orientation === 'vertical' ? '0' : rem(0))};
  width: 100%;
`;

const NumberedSegment = styled.div.withConfig({
  shouldForwardProp: (p) => !['$state', '$clickable', '$orientation', '$isLast'].includes(String(p)),
})<{ $state: StepState; $clickable: boolean; $orientation: StepsOrientation; $isLast: boolean }>`
  display: flex;
  flex-direction: ${({ $orientation }) => ($orientation === 'vertical' ? 'row' : 'column')};
  align-items: ${({ $orientation }) => ($orientation === 'vertical' ? 'flex-start' : 'center')};
  gap: ${({ $orientation }) => ($orientation === 'vertical' ? rem(14) : rem(10))};
  flex: ${({ $orientation }) => ($orientation === 'vertical' ? '0 0 auto' : '1 1 0')};
  padding: ${({ $orientation }) =>
    $orientation === 'vertical' ? `${rem(14)} 0` : `0 ${rem(8)}`};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  position: relative;

  ${({ $orientation, $isLast }) =>
    !$isLast &&
    $orientation === 'vertical' &&
    css`
      border-left: 2px solid var(--ui-border-muted);
      margin-left: ${rem(15)};
      padding-left: ${rem(26)};
    `}
`;

const NumberedBadge = styled.div.withConfig({
  shouldForwardProp: (p) => !['$state'].includes(String(p)),
})<{ $state: StepState }>`
  width: ${rem(32)};
  height: ${rem(32)};
  border-radius: var(--ui-radius-md, ${rem(8)});
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-weight: 700;
  font-size: ${rem(14)};
  transition: all 0.2s ease;

  ${({ $state }) => {
    switch ($state) {
      case 'completed':
        return css`
          background: var(--ui-interactive);
          color: var(--ui-on-interactive);
        `;
      case 'active':
        return css`
          background: var(--ui-surface);
          color: var(--ui-interactive);
          border: 2px solid var(--ui-interactive);
        `;
      case 'upcoming':
      default:
        return css`
          background: var(--ui-background-muted);
          color: var(--ui-text-subtle);
        `;
    }
  }}
`;

const NumberedConnectorH = styled.div.withConfig({
  shouldForwardProp: (p) => !['$completed'].includes(String(p)),
})<{ $completed?: boolean }>`
  flex: 1 1 auto;
  height: ${rem(2)};
  background: ${({ $completed }) =>
    $completed ? 'var(--ui-interactive)' : 'var(--ui-border-muted)'};
  border-radius: var(--ui-radius-sm, ${rem(1)});
  margin-top: ${rem(15)};
  transition: background-color 0.3s ease;
`;

const NumberedMeta = styled.div<{ $orientation: StepsOrientation }>`
  display: flex;
  flex-direction: column;
  gap: ${rem(2)};
  text-align: ${({ $orientation }) => ($orientation === 'vertical' ? 'left' : 'center')};
`;

const NumberedLabel = styled.span.withConfig({
  shouldForwardProp: (p) => !['$state'].includes(String(p)),
})<{ $state: StepState }>`
  font-size: ${rem(14)};
  font-weight: ${({ $state }) => ($state === 'active' ? 600 : 500)};
  color: ${({ $state }) =>
    $state === 'upcoming' ? 'var(--ui-text-subtle)' : 'var(--ui-text)'};
`;

const NumberedDesc = styled.span`
  font-size: ${rem(12)};
  color: var(--ui-text-subtle);
  line-height: 1.35;
`;

const NumberedCheckIcon = styled.span`
  font-size: 16px;
  line-height: 1;
  animation: ${scaleIn} 0.25s ease;
`;

/* ═══════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════ */

export const Steps = React.forwardRef<HTMLDivElement, StepsProps>(function Steps(
  {
    steps,
    currentStep,
    variant = 'default',
    orientation = 'horizontal',
    onStepClick,
    semanticId,
    size = 'md',
    showDescription = true,
    showConnector = true,
    ...rest
  },
  ref,
) {
  const semanticProps = getSemanticAttributes(semanticId);
  const clickable = Boolean(onStepClick);

  /* ── Compact variant ── */
  if (variant === 'compact') {
    return (
      <CompactNav ref={ref as React.Ref<HTMLElement>} {...semanticProps} {...rest}>
        {steps.map((step, index) => {
          const isActive = index <= currentStep - 1;
          const isClickable = clickable && isActive && index < currentStep - 1;
          return (
            <React.Fragment key={`${step.label}-${index}`}>
              <CompactItem
                $active={isActive}
                $clickable={isClickable || Boolean(onStepClick)}
                onClick={() => onStepClick?.(index)}
                role="listitem"
              >
                <CompactCircle $active={isActive} $size={size} className="compact-circle">
                  <span>{index + 1}</span>
                </CompactCircle>
                <CompactLabel $active={isActive} $size={size} className="compact-label">
                  {step.label}
                </CompactLabel>
              </CompactItem>
              {index < steps.length - 1 && (
                <CompactSeparator $size={size} className="material-symbols-outlined" aria-hidden="true">
                  arrow_forward_ios
                </CompactSeparator>
              )}
            </React.Fragment>
          );
        })}
      </CompactNav>
    );
  }

  /* ── Pill variant ── */
  if (variant === 'pill') {
    return (
      <PillRoot ref={ref} $orientation={orientation} role="list" {...semanticProps} {...rest}>
        {steps.map((step, index) => {
          const state = getStepState(index, currentStep);
          return (
            <PillSegment key={`${step.label}-${index}`} $orientation={orientation} role="listitem">
              <PillBar
                $state={state}
                $clickable={clickable}
                $orientation={orientation}
                onClick={() => onStepClick?.(index)}
              />
              <PillMeta $orientation={orientation}>
                <PillNumber $state={state}>Step {index + 1}</PillNumber>
                <PillLabel $state={state}>{step.label}</PillLabel>
                {showDescription && step.description && (
                  <PillDesc>{step.description}</PillDesc>
                )}
              </PillMeta>
            </PillSegment>
          );
        })}
      </PillRoot>
    );
  }

  /* ── Dot variant ── */
  if (variant === 'dot') {
    return (
      <DotRoot ref={ref} $orientation={orientation} role="list" {...semanticProps} {...rest}>
        {steps.map((step, index) => {
          const state = getStepState(index, currentStep);
          return (
            <DotStep
              key={`${step.label}-${index}`}
              $clickable={clickable}
              $orientation={orientation}
              role="listitem"
              onClick={() => onStepClick?.(index)}
            >
              <DotIndicator $state={state} data-state={state} />
              <DotMeta $orientation={orientation}>
                <DotLabel $state={state}>{step.label}</DotLabel>
                {showDescription && step.description && (
                  <DotDesc>{step.description}</DotDesc>
                )}
              </DotMeta>
            </DotStep>
          );
        })}
      </DotRoot>
    );
  }

  /* ── Numbered variant ── */
  if (variant === 'numbered') {
    if (orientation === 'horizontal') {
      return (
        <NumberedRoot ref={ref} $orientation="horizontal" role="list" {...semanticProps} {...rest}>
          {steps.map((step, index) => {
            const state = getStepState(index, currentStep);
            const isLast = index === steps.length - 1;
            return (
              <React.Fragment key={`${step.label}-${index}`}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '0 0 auto' }} role="listitem">
                  <NumberedBadge
                    $state={state}
                    data-state={state}
                    onClick={() => onStepClick?.(index)}
                    style={{ cursor: clickable ? 'pointer' : 'default' }}
                  >
                    {state === 'completed' ? (
                      <NumberedCheckIcon className="material-symbols-outlined">check</NumberedCheckIcon>
                    ) : (
                      index + 1
                    )}
                  </NumberedBadge>
                  <NumberedMeta $orientation="horizontal" style={{ marginTop: rem(8) }}>
                    <NumberedLabel $state={state}>{step.label}</NumberedLabel>
                    {showDescription && step.description && (
                      <NumberedDesc>{step.description}</NumberedDesc>
                    )}
                  </NumberedMeta>
                </div>
                {!isLast && showConnector && <NumberedConnectorH $completed={index < currentStep} aria-hidden />}
              </React.Fragment>
            );
          })}
        </NumberedRoot>
      );
    }

    return (
      <NumberedRoot ref={ref} $orientation="vertical" role="list" {...semanticProps} {...rest}>
        {steps.map((step, index) => {
          const state = getStepState(index, currentStep);
          const isLast = index === steps.length - 1;
          return (
            <NumberedSegment
              key={`${step.label}-${index}`}
              $state={state}
              $clickable={clickable}
              $orientation="vertical"
              $isLast={isLast}
              role="listitem"
              onClick={() => onStepClick?.(index)}
            >
              <NumberedBadge $state={state} data-state={state} style={{ position: 'relative', left: orientation === 'vertical' ? rem(-27) : '0', zIndex: 1 }}>
                {state === 'completed' ? (
                  <NumberedCheckIcon className="material-symbols-outlined">check</NumberedCheckIcon>
                ) : (
                  index + 1
                )}
              </NumberedBadge>
              <NumberedMeta $orientation="vertical">
                <NumberedLabel $state={state}>{step.label}</NumberedLabel>
                {showDescription && step.description && (
                  <NumberedDesc>{step.description}</NumberedDesc>
                )}
              </NumberedMeta>
            </NumberedSegment>
          );
        })}
      </NumberedRoot>
    );
  }

  /* ── Default variant ── */
  if (orientation === 'vertical') {
    return (
      <DefaultRoot ref={ref} $orientation="vertical" role="list" {...semanticProps} {...rest}>
        {steps.map((step, index) => {
          const state = getStepState(index, currentStep);
          return (
            <DefaultSegment key={`${step.label}-${index}`} $orientation="vertical" role="listitem">
              <DefaultStepColumn $orientation="vertical">
                <DefaultCircle
                  $state={state}
                  $clickable={clickable}
                  $sz={size}
                  data-state={state}
                  onClick={() => onStepClick?.(index)}
                  aria-current={state === 'active' ? 'step' : undefined}
                >
                  {state === 'completed' ? (
                    <DefaultCheckIcon $sz={size} className="material-symbols-outlined">
                      check
                    </DefaultCheckIcon>
                  ) : (
                    index + 1
                  )}
                </DefaultCircle>
                <DefaultBody $orientation="vertical">
                  <DefaultTitle $sz={size}>{step.label}</DefaultTitle>
                  {showDescription && step.description && (
                    <DefaultDesc $sz={size}>{step.description}</DefaultDesc>
                  )}
                </DefaultBody>
              </DefaultStepColumn>
              {index < steps.length - 1 && showConnector && (
                <DefaultConnectorV $completed={index < currentStep} $sz={size} aria-hidden />
              )}
            </DefaultSegment>
          );
        })}
      </DefaultRoot>
    );
  }

  return (
    <DefaultRoot ref={ref} $orientation="horizontal" role="list" {...semanticProps} {...rest}>
      {steps.map((step, index) => {
        const state = getStepState(index, currentStep);
        return (
          <DefaultSegment key={`${step.label}-${index}`} $orientation="horizontal" role="listitem">
            <DefaultStepColumn $orientation="horizontal">
              <DefaultCircle
                $state={state}
                $clickable={clickable}
                $sz={size}
                data-state={state}
                onClick={() => onStepClick?.(index)}
                aria-current={state === 'active' ? 'step' : undefined}
              >
                {state === 'completed' ? (
                  <DefaultCheckIcon $sz={size} className="material-symbols-outlined">
                    check
                  </DefaultCheckIcon>
                ) : (
                  index + 1
                )}
              </DefaultCircle>
              <DefaultBody $orientation="horizontal">
                <DefaultTitle $sz={size}>{step.label}</DefaultTitle>
                {showDescription && step.description && (
                  <DefaultDesc $sz={size}>{step.description}</DefaultDesc>
                )}
              </DefaultBody>
            </DefaultStepColumn>
            {index < steps.length - 1 && showConnector && (
              <DefaultConnectorH $completed={index < currentStep} $sz={size} aria-hidden />
            )}
          </DefaultSegment>
        );
      })}
    </DefaultRoot>
  );
});

Steps.displayName = 'Steps';
